import { useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Loader2, Send, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { STUDIO_TOS, hashStudioTos } from "@shared/commission";
import { useCommissionStore } from "../../stores/commissionStore";
import { useUploadStore } from "../../stores/uploadStore";
import { useIntakeCopy } from "../../i18n/useIntakeCopy";
import { buildPayload } from "../../lib/buildPayload";
import { honeypot } from "../../lib/honeypot";
import { MiniMarkdown } from "../../lib/miniMarkdown";
import { submitIntake, IntakeApiError } from "../../api/intakeApi";
import { useChecklist } from "../ArtistChecklist";
import { Recap, recapFromDraft } from "../Recap";
import { SuccessScreen } from "../SuccessScreen";
import { Shake } from "../../motion/primitives";
import { SPRING_TOUCH } from "../../motion/tokens";
import type { IntakeArtist } from "../../types";

type Status = { kind: "idle" } | { kind: "sending" } | { kind: "done"; trackingUrl: string } | { kind: "error"; message: string };

export function ReviewTermsStep({ artist }: { artist: IntakeArtist }) {
  const { c, fmt, locale } = useIntakeCopy();
  const qc = useQueryClient();
  const draft = useCommissionStore();
  const pendingUploads = useUploadStore((u) => u.pending);
  const { items } = useChecklist(artist);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [termsUpdated, setTermsUpdated] = useState(false);
  const [shake, setShake] = useState(0);

  if (status.kind === "done") {
    return <SuccessScreen artistName={artist.name} trackingUrl={status.trackingUrl}
      onAnother={() => { setStatus({ kind: "idle" }); draft.goTo("type"); }} />;
  }

  const requiredOk = items.filter((i) => i.required && i.id !== "terms").every((i) => i.done);
  const canSend = requiredOk && draft.tosAccepted && pendingUploads === 0 && status.kind !== "sending";

  async function send() {
    setStatus({ kind: "sending" });
    try {
      // Hash the studio terms this bundle actually showed, so a stale tab can't accept terms it never rendered.
      const payload = {
        ...buildPayload(useCommissionStore.getState(), artist, locale),
        studio_tos_hash: await hashStudioTos(),
        website: honeypot.current,
      };
      const res = await submitIntake(payload);
      useCommissionStore.getState().resetAfterSubmission();
      setStatus({ kind: "done", trackingUrl: res.tracking_url });
    } catch (err) {
      const e = err instanceof IntakeApiError ? err : new IntakeApiError(0, "network");
      setShake((n) => n + 1);
      if (e.status === 409) {
        await qc.invalidateQueries({ queryKey: ["intake-artist", artist.slug] });
        useCommissionStore.getState().setTosAccepted(false);
        setTermsUpdated(true);
        setStatus({ kind: "idle" });
        return;
      }
      if (e.status === 400 && e.details) {
        const first = Object.keys(e.details)[0] ?? "";
        const step = first.startsWith("client") || first === "posting_permission" ? "contact"
          : first.startsWith("intake") ? "characters"
          : first === "commission_type" || first === "character_count" ? "type"
          : "scene";
        useCommissionStore.getState().goTo(step);
      }
      const message = e.status === 400 ? c.errors.validation
        : e.status === 429 ? fmt(c.errors.rateLimited, { n: e.retryAfter ?? 60 })
        : c.errors.network;
      setStatus({ kind: "error", message });
    }
  }

  const terms = STUDIO_TOS[locale];
  return (
    <section className="space-y-6">
      <h2 data-step-heading tabIndex={-1} className="font-(family-name:--font-display) font-normal text-3xl text-[var(--color-text)] outline-none">{c.review.question}</h2>

      <Recap data={recapFromDraft(draft)} onEdit={draft.goTo} />

      <div className="space-y-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] p-4 sm:p-5">
        <AnimatePresence>{termsUpdated && (
          <m.p role="alert" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-lg bg-[var(--color-warning)]/10 px-3 py-2 text-sm text-[var(--color-warning)]">{c.review.termsUpdated}</m.p>
        )}</AnimatePresence>
        <div>
          <h3 className="mb-2 font-medium text-[var(--color-text)]">{c.review.studioTerms}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {terms.map((sec) => (
              <div key={sec.title}>
                <p className="text-xs uppercase tracking-wide text-[var(--color-text-muted)]">{sec.title}</p>
                <ul className="mt-1 space-y-0.5 text-sm text-[var(--color-text-muted)]">{sec.items.map((it) => <li key={it}>• {it}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
        {artist.tosMarkdown && (
          <div className="border-t border-[var(--color-border)] pt-4">
            <h3 className="mb-2 font-medium text-[var(--color-text)]">{fmt(c.review.artistTerms, { artist: artist.name })}</h3>
            <MiniMarkdown source={artist.tosMarkdown} />
          </div>
        )}
        <label className="flex cursor-pointer items-center gap-3 border-t border-[var(--color-border)] pt-4">
          <input type="checkbox" className="h-5 w-5 accent-[var(--color-primary)]" checked={draft.tosAccepted}
            onChange={(e) => { draft.setTosAccepted(e.target.checked); if (e.target.checked) setTermsUpdated(false); }} />
          <span className="text-[var(--color-text)]">{c.review.accept}</span>
        </label>
      </div>

      <AnimatePresence>{status.kind === "error" && (
        <m.div role="alert" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="flex items-center justify-between gap-3 rounded-lg border border-[var(--color-error)]/40 bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-text)]">
          <span>{status.message}</span>
          <button type="button" onClick={send} className="font-medium text-[var(--color-primary)] hover:underline">{c.errors.retry}</button>
        </m.div>
      )}</AnimatePresence>

      <div className="flex items-center justify-between border-t border-[var(--color-border)] pt-6">
        <button type="button" onClick={draft.back} className="text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]">← {c.nav.back}</button>
        <Shake trigger={shake}>
          <m.button type="button" layout onClick={send} disabled={!canSend} transition={SPRING_TOUCH} whileTap={{ scale: 0.97 }}
            className="relative flex h-12 items-center gap-2 overflow-hidden rounded-full bg-[var(--color-primary)] px-7 font-semibold text-[var(--color-bg)] disabled:opacity-40">
            {canSend && status.kind === "idle" && (
              <m.span aria-hidden className="pointer-events-none absolute inset-0 rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent)" }}
                initial={{ x: "-100%" }} animate={{ x: "100%" }} transition={{ duration: 1.4, ease: "easeInOut", repeat: 1 }} />
            )}
            {status.kind === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" />{c.nav.sending}</>
              : draft.tosAccepted && requiredOk ? <><Send className="h-4 w-4" />{c.nav.send}</>
              : <><Check className="h-4 w-4 opacity-50" />{c.nav.send}</>}
          </m.button>
        </Shake>
      </div>
    </section>
  );
}
